import { UrlParamName } from "search-params";

/**
 * Transient booking params that should not be carried forward after a booking is completed.
 * Used as a base exclude list by both finalize and payment-terminal flows.
 */
const TRANSIENT_BOOKING_PARAMS: UrlParamName[] = [
    UrlParamName.RULE_ID,
    UrlParamName.DATE,
    UrlParamName.GUESTS,
    UrlParamName.GUESTS_CHILDREN,
    UrlParamName.IS_TIMES_CONFIRMED,
    UrlParamName.IS_WAIT_LIST,
];

/**
 * Payment-terminal-specific params injected by the payment provider (PayEx) redirect.
 * Only meaningful during the payment-confirmation step — must not leak into a new booking session.
 */
const PAYMENT_TERMINAL_PARAMS: UrlParamName[] = [
    UrlParamName.WEB_BOOKING_ID,
    UrlParamName.CHARGE_ID,
    UrlParamName.CAPTURED,
    UrlParamName.IS_NO_SHOW,
    UrlParamName.OFFER_START_TIME,
];

/**
 * Filters URL search params, removing the specified keys using case-insensitive matching.
 * Case-insensitive matching is necessary because payment providers (e.g. PayEx) may redirect
 * with lowercase variants of param names (e.g. "chargeid" instead of "ChargeId").
 *
 * @param search - The current URL search string (e.g. `window.location.search`)
 * @param excludeParams - List of `UrlParamName` values to strip from the search string
 * @returns Filtered query string without the leading `?`, or an empty string if no params remain
 *
 * @example
 * const kept = filterSearchParams(window.location.search, [
 *     ...TRANSIENT_BOOKING_PARAMS,
 *     ...PAYMENT_TERMINAL_PARAMS,
 * ]);
 * navigate({ pathname, search: kept ? `?${kept}` : "" });
 */
function filterSearchParams(search: string, excludeParams: UrlParamName[]): string {
    const excludeLower = excludeParams.map((p) => p.toLowerCase());
    return Array.from(new URLSearchParams(search).entries())
        .filter(([key]) => !excludeLower.includes(key.toLowerCase()))
        .reduce((params, [key, value]) => {
            params.set(key, value);
            return params;
        }, new URLSearchParams())
        .toString();
}

export { TRANSIENT_BOOKING_PARAMS, PAYMENT_TERMINAL_PARAMS, filterSearchParams };
